import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define the settings interface
interface Settings {
  // Layout settings
  sidebarCollapsed: boolean;
  sidebarPosition: 'left' | 'right';
  sidebarTheme: 'light' | 'dark';
  
  // Header settings
  headerTheme: 'light' | 'dark';
  headerFixed: boolean;
  
  // Content settings
  contentTheme: 'light' | 'dark';
  contentLayout: 'fluid' | 'boxed';
  
  // Theme settings
  theme: 'light' | 'dark' | 'system';
  
  // User preferences
  language: string;
  timezone: string;
  
  // Feature flags
  enableNotifications: boolean;
  enableAnimations: boolean;
}

// Default settings
const defaultSettings: Settings = {
  sidebarCollapsed: false,
  sidebarPosition: 'left',
  sidebarTheme: 'dark',
  headerTheme: 'light',
  headerFixed: true,
  contentTheme: 'light',
  contentLayout: 'fluid',
  theme: 'light',
  language: 'en',
  timezone: 'UTC',
  enableNotifications: true,
  enableAnimations: true,
};

// Settings context
interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => void;
  toggleSidebar: () => void;
  setTheme: (theme: Settings['theme']) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// Settings provider component
interface SettingsProviderProps {
  children: ReactNode;
}

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [settings, setSettings] = useState<Settings>(() => {
    // Load settings from localStorage on initialization
    const savedSettings = localStorage.getItem('app-settings');
    if (savedSettings) {
      try {
        return { ...defaultSettings, ...JSON.parse(savedSettings) };
      } catch (error) {
        console.error('Failed to parse saved settings:', error);
      }
    }
    return defaultSettings;
  });

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('app-settings', JSON.stringify(settings));
  }, [settings]);

  // Update settings function
  const updateSettings = (newSettings: Partial<Settings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  // Toggle sidebar function
  const toggleSidebar = () => {
    setSettings(prev => ({ ...prev, sidebarCollapsed: !prev.sidebarCollapsed }));
  };

  // Set theme function
  const setTheme = (theme: Settings['theme']) => {
    setSettings(prev => ({ ...prev, theme }));
  };

  const value: SettingsContextType = {
    settings,
    updateSettings,
    toggleSidebar,
    setTheme,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

// Custom hook to use settings
export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
